import { useRouteMatch } from 'react-router-dom'
import { useRef } from 'react'

import { useAppSelector, isBusinessAccountSelector } from 'store'
import { UnityHubChannelIcon, UnityHubRoomNavLink } from 'App/components/UnityHub'
import { TChannelVariant } from 'interfaces'

import { useKebabMenu } from '../useKebabMenu'

type TCommunityChannelRoomProps = {
  chatRoomId: string
  communityId: string
  variant: Extract<TChannelVariant, 'chat' | 'news'>
  isFirstInGroup?: boolean
} & ReturnType<typeof useKebabMenu>

export const CommunityChannelRoom = ({
  chatRoomId,
  variant,
  communityId,
  getMenuItems,
  isFirstInGroup = false
}: TCommunityChannelRoomProps) => {
  const { url } = useRouteMatch()
  const roomRef = useRef<HTMLDivElement>(null)

  const { name, mutedUntil, channelManagers, inviteToken, isPublic } =
    useAppSelector((state) => state.chat.chatRooms.list[chatRoomId]) ?? {}

  const { count, mentionCount } = useAppSelector(
    (state) =>
      state.notifications.general.communities.list?.[communityId]?.chats?.[chatRoomId] ?? {}
  )

  const isBusinessAccount = useAppSelector(isBusinessAccountSelector)

  const isMuted = Boolean(mutedUntil)

  const handleEditSuccess = () =>
    roomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })

  if (!name) {
    return null
  }

  return (
    <UnityHubRoomNavLink
      to={`${url}/${variant}/${chatRoomId}`}
      icon={<UnityHubChannelIcon variant={variant} isMuted={isMuted} />}
      name={name}
      isMuted={isMuted}
      count={count}
      isPublic={isPublic && isBusinessAccount}
      mentionCount={mentionCount}
      ref={roomRef}
      menuItems={getMenuItems({
        variant,
        channelId: chatRoomId,
        channelName: name,
        channelManagerIds: channelManagers?.map((manager) => manager.id),
        inviteToken,
        isMuted,
        isPublic,
        isFirstInGroup,
        onEditSuccess: handleEditSuccess
      })}
    />
  )
}
